import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from "../ui/card";
import { ScrollArea } from "../ui/scroll-area";
import { supportedLanguages } from '../supportedLanguages';

const PrivacyPolicy = () => {
  const { lang } = useParams();
  const language = lang && supportedLanguages.includes(lang) ? lang : 'fr';

  const { t, i18n } = useTranslation('');
  React.useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language, i18n]);

  const canonicalUrl = `https://bible4.me/${language}/privacy`;

  return (
    <>
      <Helmet>
        <title>{t('privacy.title')} — Bible4Me</title>
        <meta name="description" content={t('seo.privacy.description')} />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
    <div className="min-h-screen bg-amber-100 p-4 md:p-8">
      <Card className="max-w-4xl mx-auto bg-white">
        <CardContent className="p-6">
          <ScrollArea className="h-[calc(100vh-200px)]">
            <div className="space-y-6">
              <header>
                <h1 className="text-2xl font-bold text-amber-900 mb-2">
                  {t('privacy.title')}
                </h1>
                <p className="text-amber-800">
                  {t('privacy.lastUpdated', { date: new Date() })}
                </p>
              </header>

              <section className="space-y-4">
                <h2 className="text-xl font-semibold text-amber-900">
                  {t('privacy.sections.introduction.title')}
                </h2>
                <p className="text-amber-900">
                  {t('privacy.sections.introduction.content')}
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-xl font-semibold text-amber-900">
                  {t('privacy.sections.dataCollection.title')}
                </h2>
                <div className="space-y-2">
                  <h3 className="text-lg font-medium text-amber-900">
                    {t('privacy.sections.dataCollection.provided.title')}
                  </h3>
                  <ul className="list-disc pl-6 text-amber-900">
                    {(t('privacy.sections.dataCollection.provided.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>

                  <h3 className="text-lg font-medium text-amber-900">
                    {t('privacy.sections.dataCollection.automatic.title')}
                  </h3>
                  <ul className="list-disc pl-6 text-amber-900">
                    {(t('privacy.sections.dataCollection.automatic.items', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              </section>

              <section className="space-y-4">
                <h2 className="text-xl font-semibold text-amber-900">
                  {t('privacy.sections.dataUsage.title')}
                </h2>
                <p className="text-amber-900">{t('privacy.sections.dataUsage.intro')}</p>
                <ul className="list-disc pl-6 text-amber-900">
                  {(t('privacy.sections.dataUsage.purposes', { returnObjects: true }) as string[]).map((purpose: string, index: number) => (
                    <li key={index}>{purpose}</li>
                  ))}
                </ul>
              </section>

              <section className="space-y-4">
                <h2 className="text-xl font-semibold text-amber-900">
                  {t('privacy.sections.childrenPrivacy.title')}
                </h2>
                <p className="text-amber-900">
                  {t('privacy.sections.childrenPrivacy.content')}
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-xl font-semibold text-amber-900">
                  {t('privacy.sections.security.title')}
                </h2>
                <p className="text-amber-900">
                  {t('privacy.sections.security.content')}
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-xl font-semibold text-amber-900">
                  {t('privacy.sections.dataSharing.title')}
                </h2>
                <p className="text-amber-900">
                  {t('privacy.sections.dataSharing.intro')}
                </p>
                <ul className="list-disc pl-6 text-amber-900">
                  {(t('privacy.sections.dataSharing.sharing', { returnObjects: true }) as string[]).map((item: string, index: number) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </section>

              <section className="space-y-4">
                <h2 className="text-xl font-semibold text-amber-900">
                  {t('privacy.sections.userRights.title')}
                </h2>
                <p className="text-amber-900">{t('privacy.sections.userRights.intro')}</p>
                <ul className="list-disc pl-6 text-amber-900">
                  {(t('privacy.sections.userRights.rights', { returnObjects: true }) as string[]).map((right: string, index: number) => (
                    <li key={index}>{right}</li>
                  ))}
                </ul>
              </section>

              <section className="space-y-4">
                <h2 className="text-xl font-semibold text-amber-900">
                  {t('privacy.sections.updates.title')}
                </h2>
                <p className="text-amber-900">
                  {t('privacy.sections.updates.content')}
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-xl font-semibold text-amber-900">
                  {t('privacy.sections.contact.title')}
                </h2>
                <p className="text-amber-900">
                  {t('privacy.sections.contact.content')}
                </p>
                <p className="text-amber-900">
                  {t('privacy.sections.contact.email')}<br />
                  {t('privacy.sections.contact.website')}
                </p>
              </section>
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
    </>
  );
};

export default PrivacyPolicy;